import { ReactNode } from 'react';
import { StyledGridSpan } from './StyledGrid';
import { AccessibilityProps } from '../Common/boxUtils';
import { toTrackCount } from './trackCount';

export type GridSpanProps = {
  children?: ReactNode;
  /**
   * Number of columns to span, or `'full'` to span the entire row.
   * - `number` — `grid-column: span N`
   * - `'full'` — `grid-column: 1 / -1`
   */
  columns?: number | 'full';
  /** Number of rows to span (`grid-row: span N`). */
  rows?: number;
  /** Use `'li'` when the surrounding `Grid` is a `'ul'` or `'ol'`. */
  as?: 'div' | 'section' | 'article' | 'li';
} & AccessibilityProps;

const resolveGridColumn = (columns?: number | 'full'): string | undefined => {
  if (columns === undefined) return undefined;
  if (columns === 'full') return '1 / -1';
  const count = toTrackCount(columns);
  if (count === null) return undefined;
  return `span ${count}`;
};

const resolveGridRow = (rows?: number): string | undefined => {
  if (rows === undefined) return undefined;
  const count = toTrackCount(rows);
  if (count === null) return undefined;
  return `span ${count}`;
};

/**
 * Spans a grid item across multiple columns or rows. Belongs inside a `Grid` —
 * `grid-column`/`grid-row` have no effect outside a grid container.
 */
export const GridSpan = ({
  children,
  columns,
  rows,
  ...rest
}: GridSpanProps) => (
  <StyledGridSpan
    $gridColumn={resolveGridColumn(columns)}
    $gridRow={resolveGridRow(rows)}
    {...rest}
  >
    {children}
  </StyledGridSpan>
);
